import { GetMediaControls, MediaListener } from "./ColorFinderFootageDisplay"
import { FunctionComponent, useCallback, useEffect, useRef } from "react"
import { DrawPoint, useDrawingOverlay } from "plugins/shared/useMapDrawingOverlay"
import { ColorFilter } from "./ColorFinderPanel"
import { IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { ColorFinderTiler } from "./ColorFinderTiler.class"
import { CumulativeRGB } from "@nova/component-color-finder-legend/src/web-workers/createCumulativeRgb/createCumulativeRgb"
import {
    cumulativeRgbPlotData,
    initializeColorplot,
    PLOT_SIZE,
    updateColorplot,
} from "./filter/colorplot"
import { useAppDispatch } from "integrations/redux/hooks"
import { setColorFinderTool } from "./redux/slice"
import { ColorSampleFilter } from "./ColorSample"
import { Popup } from "@nova/component-popup"
import { get } from "nodash"
import { colorKeyToRGB } from "@nova/component-color-finder-legend/src/colorspace"
import { createConcaveHullPolygon } from "@nova/calculate-geospatial"
import { useKeyboardShortcuts } from "panels/controls/useKeyboardShortcuts"
interface ColorFinderFilterProps {
    tiler: ColorFinderTiler
    getMediaControls: GetMediaControls
    onFilterChange: (newFilter: ColorFilter) => void
    onRemoveColorSample: (...ids: string[]) => void
    cumulativeRgb: CumulativeRGB
    activeSamples: ColorSampleFilter[]
}

const ColorFinderFilter: FunctionComponent<ColorFinderFilterProps> = ({
    getMediaControls,
    tiler,
    onFilterChange,
    cumulativeRgb,
    activeSamples,
    onRemoveColorSample,
}) => {
    const dispatch = useAppDispatch()

    const colorplotCanvasRef = useRef<HTMLCanvasElement>(null)

    const { undo, redo, clearDrawings } = useDrawingOverlay(() => colorplotCanvasRef.current, {
        active: true,
        options: {
            strokeStyle: "#FFFFFF",
            fillStyle: "#FFFFFF67",
            paddingX: 20,
            paddingY: 8,
            infiniteDraw: true,
        },
        onFinish: (_, drawings) => {
            const controls = getMediaControls()
            if (!controls || !cumulativeRgb?.allPoints) return
            const filtered = new Set<number>()

            for (let i = 0; i < drawings.length; i++) {
                const drawing = drawings[i]
                cumulativeRgb.allPoints.forEach((c) => {
                    const colorIsIncluded = isPointInPolygon(c, drawing)
                    const colorKey = (c.rgb[0] >> 3) * 1024 + (c.rgb[1] >> 3) * 32 + (c.rgb[2] >> 3)
                    if (colorIsIncluded) {
                        filtered.add(colorKey)
                    }
                })
            }
            onFilterChange(filtered)
        },
    })

    const { setDrawings: setColorSampleDrawings, clearDrawings: clearColorSampleDrawings } =
        useDrawingOverlay(() => colorplotCanvasRef.current, {
            active: true,
            options: { strokeStyle: "#FFFFFF", fillStyle: "#FFFFFF67" },
        })

    useKeyboardShortcuts({ onUndo: undo, onRedo: redo, onDelete: clearDrawings })

    useEffect(() => {
        clearColorSampleDrawings()
        const xys = activeSamples.map(colorSampleToDrawPoints)
        setColorSampleDrawings(xys)
    }, [activeSamples, clearColorSampleDrawings, setColorSampleDrawings])

    const drawColorPlot = useCallback(
        (frame: number) => {
            if (colorplotCanvasRef.current && cumulativeRgb) {
                updateColorplot(
                    tiler,
                    cumulativeRgb.colorplotPoints,
                    colorplotCanvasRef.current,
                    frame
                )
            }
        },
        [cumulativeRgb, tiler]
    )

    useEffect(() => {
        const controls = getMediaControls()
        if (!controls) return

        if (colorplotCanvasRef.current) {
            initializeColorplot(colorplotCanvasRef.current)
        }
        const listener: MediaListener = (state) => {
            drawColorPlot(state.frame)
        }

        const addTileLoadListener = () => {
            if (!controls) return
            drawColorPlot(controls.getFrame())
        }

        tiler.addTileLoadListener(addTileLoadListener)

        drawColorPlot(controls.getFrame())

        controls?.subscribe(listener)

        return () => {
            controls?.unsubscribe(listener)
            tiler.removeTileLoadListener(addTileLoadListener)
        }
    }, [cumulativeRgb, drawColorPlot, getMediaControls, tiler])

    const handleClearFilters = () => {
        clearDrawings()
        onRemoveColorSample(...activeSamples.map((s) => `${get(s, "id")}`))
    }

    return (
        <div className="flex flex-col w-full">
            <div className="w-full h-full py-2">
                <canvas
                    style={{ height: PLOT_SIZE, width: PLOT_SIZE }}
                    id="colorplot-canvas"
                    className="m-auto"
                    ref={colorplotCanvasRef}
                />
            </div>
            <div className="flex justify-between items-center px-3">
                <div className="flex gap-1">
                    <IconButton onClick={() => dispatch(setColorFinderTool("color-sample"))}>
                        <Icons.SmallIcon Icon={Icons.ImageSearch} />
                    </IconButton>
                    {!!activeSamples.length && (
                        <Popup
                            position="bottom-right"
                            content={
                                <Popup.PopupModal>
                                    <ActiveColorSamples
                                        activeSamples={activeSamples}
                                        onRemoveSample={onRemoveColorSample}
                                    />
                                </Popup.PopupModal>
                            }
                        >
                            <IconButton className="px-2 py-1 bg-blue-700 text-gray-0 rounded-md">
                                <small>{activeSamples.length}</small>
                            </IconButton>
                        </Popup>
                    )}
                </div>
                <div className="flex gap-3">
                    <IconButton onClick={undo}>
                        <Icons.SmallIcon Icon={Icons.Undo} />
                    </IconButton>
                    <IconButton onClick={redo}>
                        <Icons.SmallIcon Icon={Icons.Redo} />
                    </IconButton>
                    <IconButton onClick={handleClearFilters}>
                        <Icons.SmallIcon Icon={Icons.RestartAlt} />
                    </IconButton>
                </div>
            </div>
        </div>
    )
}

const ActiveColorSamples = ({
    activeSamples,
    onRemoveSample,
}: {
    activeSamples: ColorSampleFilter[]
    onRemoveSample: (id: string) => void
}) => {
    return (
        <div className="flex flex-col gap-1.5 w-[208px]">
            <small className="">ACTIVE COLOR SAMPLES</small>
            <div className="flex flex-col divide-y divide-gray-700">
                {activeSamples.map((sample) => (
                    <div
                        className="flex justify-between w-full py-2.5 first:pt-0 last:pb-0"
                        key={get(sample, "id") as string}
                    >
                        <div className="flex gap-2 items-center">
                            <div
                                className="aspect-square h-6 w-6 rounded-md"
                                style={{
                                    background: `linear-gradient(to right, ${sample.from}, ${sample.to})`,
                                }}
                            />
                            <h5 className="text-gray-100">
                                {sample.from} - {sample.to}
                            </h5>
                        </div>
                        <IconButton
                            className="text-gray-100"
                            onClick={() => onRemoveSample(`${get(sample, "id")}`)}
                        >
                            <Icons.SmallIcon Icon={Icons.XClose} />
                        </IconButton>
                    </div>
                ))}
            </div>
        </div>
    )
}

const colorSampleToDrawPoints = (colorSample: ColorSampleFilter) => {
    const RGBs = Array.from(colorSample.filter).map(colorKeyToRGB)
    const xys = Array.from(cumulativeRgbPlotData(RGBs).allPoints)
    const concavemanified = createConcaveHullPolygon(
        xys.map((xy) => [xy.x, xy.y]),
        2
    )

    return concavemanified.map<DrawPoint>(([x, y]) => ({
        latitude: 0,
        longitude: 0,
        x,
        y,
    }))
}

function isPointInPolygon(
    point: { x: number; y: number },
    polygon: { x: number; y: number }[]
): boolean {
    const { x, y } = point
    let isInside = false

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const vertexI = polygon[i]
        const vertexJ = polygon[j]
        const vertexIX = vertexI.x
        const vertexIY = vertexI.y
        const vertexJX = vertexJ.x
        const vertexJY = vertexJ.y

        const intersect =
            vertexI.y > y !== vertexJ.y > y &&
            x < ((vertexJX - vertexIX) * (y - vertexIY)) / (vertexJY - vertexIY) + vertexIX

        if (intersect) {
            isInside = !isInside
        }
    }

    return isInside
}

export { ColorFinderFilter }
